import React, { useEffect, useState } from 'react';
import { CardBody, Badge } from 'reactstrap';
import {
  formatAlphaPrice,
  isAlphaFilled,
  coalesceAlphaPrice,
  resolveRowCapable,
} from '../../../../../../../utils/deliveryDetailUtils';
import {
  formatIncotermRoute,
  formatFobLocationsJoined,
  isCounterValueChanged,
  isCounterQuantityChanged,
  isCounterDateChanged,
  isFobOriginDisplay,
  getCapableOriginNamesFromFobLocations,
  cellClass,
  headerCellClass,
  renderPortCell,
  renderSelectedOriginLocations,
} from '../../../../../../../../../shared/offer-domain/delivery';
import { COUNTER_ACCENT } from '../../../../../../../../../shared/offer-domain/constants';

const getTableColSpan = usesDyedUndyed => (usesDyedUndyed ? 10 : 9);

const isFobIncotermRow = data =>
  String(data?.incoterm || '')
    .trim()
    .toUpperCase() === 'FOB';

const getRowOriginNames = data =>
  (Array.isArray(data?.fobLocationNames) ? data.fobLocationNames : []).filter(
    Boolean
  );

const resolveLoadingPortLabel = data => {
  if (data?.fobOriginLocationName) {
    return data.fobOriginLocationName;
  }
  if (data?.incotermLocationId) {
    return data?.loadingPort || '-';
  }
  const names = getRowOriginNames(data);
  if (isFobOriginDisplay(data?.route, names)) {
    const capableNames = getCapableOriginNamesFromFobLocations(
      data?.incotermFOBLocations
    );
    const fallbackNames = (
      Array.isArray(data?.selectedCapableLocations)
        ? data.selectedCapableLocations
        : []
    )
      .map(loc => loc?.name)
      .filter(Boolean);
    return renderSelectedOriginLocations(
      capableNames.length ? capableNames : fallbackNames
    );
  }
  if (isFobIncotermRow(data) || names.length) {
    return formatFobLocationsJoined(names) || data?.loadingPort || '-';
  }
  return '-';
};

const getRowKey = (data, index) => {
  const base =
    data?.quantityInformationDetailId ??
    `${data?.id ?? ''}-${data?.quantityIncotermId ?? index}`;
  if (data?.incotermLocationId) {
    return `${base}-loc-${data.incotermLocationId}`;
  }
  return String(base);
};

const hasMeaningfulCounter = (data, usesDyedUndyed) =>
  !!(
    data?.quantityInformationDetailCounterId ||
    (usesDyedUndyed
      ? isAlphaFilled(data?.counterDyed) ||
        isAlphaFilled(data?.counterUndyed) ||
        isAlphaFilled(data?.dyed2) ||
        isAlphaFilled(data?.undyed2)
      : isAlphaFilled(data?.counterAlpha) || isAlphaFilled(data?.alpha2))
  );

const getIncotermGroupKey = (data, index) =>
  String(data?.quantityIncotermId ?? `row-${index}`);

const DeliveryDetailTableNonLpg = ({
  items = [],
  usesDyedUndyed = true,
  alphaColumnLabel = 'Alpha',
  pricingUnit = null,
}) => {
  const alphaSuffix = pricingUnit || 'USD/BBL';
  const usesAlphaField = !usesDyedUndyed;
  const colSpan = getTableColSpan(usesDyedUndyed);
  const [expandedRows, setExpandedRows] = useState({});

  const counterByIncotermGroup = {};
  items.forEach((data, index) => {
    const key = getIncotermGroupKey(data, index);
    counterByIncotermGroup[key] =
      counterByIncotermGroup[key] || hasMeaningfulCounter(data, usesDyedUndyed);
  });

  useEffect(() => {
    const initial = {};
    items.forEach((data, index) => {
      if (hasMeaningfulCounter(data, usesDyedUndyed)) {
        initial[getRowKey(data, index)] = true;
      }
    });
    if (!Object.keys(initial).length) return;
    setExpandedRows(prev => {
      const merged = { ...prev };
      Object.keys(initial).forEach(key => {
        if (!(key in merged)) merged[key] = true;
      });
      return merged;
    });
  }, [items, usesDyedUndyed]);

  const toggleRowExpand = rowKey => {
    setExpandedRows(prev => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  const renderCounterOfferRow = data => {
    const counterDeliveryBasis =
      data.counterDeliveryBasis ?? data.counterParcelSize ?? null;
    const deliveryBasisChanged = isCounterQuantityChanged(
      data.deliveryBasis ?? data.parcelSize,
      counterDeliveryBasis
    );
    const incotermRoute = formatIncotermRoute(
      data.counterIncoterm,
      data.counterRoute
    );
    const originalIncotermRoute = formatIncotermRoute(
      data.incoterm,
      data.route
    );
    const incotermRouteChanged =
      !!data.counterIncoterm &&
      isCounterValueChanged(originalIncotermRoute, incotermRoute);

    const counterAlphaDyed = coalesceAlphaPrice(
      data.counterDyed,
      data.dyed2,
      data.dyed,
      data.unitBbl
    );
    const counterAlphaUndyed = coalesceAlphaPrice(
      data.counterUndyed,
      data.undyed2,
      data.undyed,
      data.cbm
    );
    const counterAlpha = coalesceAlphaPrice(
      data.counterAlpha,
      data.alpha2,
      data.alphaMitra2,
      data.alpha,
      data.alphaMitra1
    );
    const alphaDyedChanged =
      usesDyedUndyed &&
      isCounterValueChanged(
        data.dyed ?? data.unitBbl,
        coalesceAlphaPrice(data.counterDyed, data.dyed2)
      );
    const alphaUndyedChanged =
      usesDyedUndyed &&
      isCounterValueChanged(
        data.undyed ?? data.cbm,
        coalesceAlphaPrice(data.counterUndyed, data.undyed2)
      );
    const alphaChanged =
      usesAlphaField &&
      isCounterValueChanged(
        data.alpha,
        coalesceAlphaPrice(data.counterAlpha, data.alpha2, data.alphaMitra2)
      );

    return (
      <tr className='bg-light'>
        <td colSpan={colSpan} className='p-0 border-0'>
          <div
            className='bg-light-subtle'
            style={{
              borderLeft: `3px solid ${COUNTER_ACCENT}`,
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <div
              className='px-2 py-2'
              style={{ width: '100%', overflowX: 'auto' }}
            >
              <table
                className='table table-sm table-borderless mb-0 align-middle w-100'
                style={{ tableLayout: 'fixed', width: '100%', minWidth: 0 }}
              >
                <thead>
                  <tr className='text-muted border-bottom'>
                    <th
                      className='fs-12 fw-semibold ps-1 pb-2'
                      style={{ width: '7%' }}
                    />
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: '11%' }}
                    >
                      Delivery Basis
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: '13%' }}
                    >
                      Incoterm - Route
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: '13%' }}
                    >
                      Port 1
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: '13%' }}
                    >
                      Port 2
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: '11%' }}
                    >
                      Port 3
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: usesDyedUndyed ? '13%' : '26%' }}
                    >
                      {usesDyedUndyed ? 'Alpha Dyed' : alphaColumnLabel}
                    </th>
                    {usesDyedUndyed && (
                      <th
                        className='fs-12 fw-semibold pb-2 pe-2'
                        style={{ width: '13%' }}
                      >
                        Alpha Undyed
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className='ps-1 py-2 align-top'>
                      <span
                        className='badge rounded-pill px-2 py-1 fw-semibold'
                        style={{
                          backgroundColor: COUNTER_ACCENT,
                          color: '#fff',
                          fontSize: '11px',
                        }}
                      >
                        Counter
                      </span>
                    </td>
                    <td
                      className={`fs-12 py-2 pe-2 align-top text-break ${deliveryBasisChanged ? 'fw-semibold' : 'fw-medium'}`}
                      style={{
                        color: deliveryBasisChanged
                          ? COUNTER_ACCENT
                          : undefined,
                        wordBreak: 'break-word',
                      }}
                    >
                      {counterDeliveryBasis != null &&
                      counterDeliveryBasis !== ''
                        ? counterDeliveryBasis
                        : '-'}
                    </td>
                    <td
                      className={`fs-12 py-2 pe-2 align-top text-break ${incotermRouteChanged ? 'fw-semibold' : ''}`}
                      style={{
                        color: incotermRouteChanged
                          ? COUNTER_ACCENT
                          : undefined,
                        wordBreak: 'break-word',
                      }}
                    >
                      {incotermRoute || '-'}
                    </td>
                    <td className='py-2 pe-2 align-top' style={{ minWidth: 0 }}>
                      {renderPortCell(
                        data.counterLoadingPort,
                        data.counterParcelSize,
                        {
                          portLabel: 'Location',
                          startDate: data.counterStartDate,
                          endDate: data.counterEndDate,
                          fallbackDate: data.counterAddAld,
                          highlight: {
                            port: isCounterValueChanged(
                              data.port1 || data.loadingPort,
                              data.counterLoadingPort
                            ),
                            parcelSize: isCounterQuantityChanged(
                              data.parcelSize,
                              data.counterParcelSize
                            ),
                            date: isCounterDateChanged(
                              data.port1StartDate,
                              data.port1EndDate,
                              data.counterStartDate,
                              data.counterEndDate,
                              data.addAld,
                              data.counterAddAld
                            ),
                          },
                        }
                      )}
                    </td>
                    <td className='py-2 pe-2 align-top' style={{ minWidth: 0 }}>
                      {renderPortCell(
                        data.counterPort2,
                        data.counterParcelSize2,
                        {
                          startDate: data.counterStartDate2,
                          endDate: data.counterEndDate2,
                          fallbackDate: data.counterAddAld2,
                          highlight: {
                            port: isCounterValueChanged(
                              data.port2,
                              data.counterPort2
                            ),
                            parcelSize: isCounterQuantityChanged(
                              data.parcelSize2,
                              data.counterParcelSize2
                            ),
                            date: isCounterDateChanged(
                              data.port2StartDate,
                              data.port2EndDate,
                              data.counterStartDate2,
                              data.counterEndDate2,
                              data.addAld2,
                              data.counterAddAld2
                            ),
                          },
                        }
                      )}
                    </td>
                    <td className='py-2 pe-2 align-top' style={{ minWidth: 0 }}>
                      {renderPortCell(
                        data.counterPort3,
                        data.counterParcelSize3,
                        {
                          startDate: data.counterStartDate3,
                          endDate: data.counterEndDate3,
                          fallbackDate: data.counterAddAld3,
                          highlight: {
                            port: isCounterValueChanged(
                              data.port3,
                              data.counterPort3
                            ),
                            parcelSize: isCounterQuantityChanged(
                              data.parcelSize3,
                              data.counterParcelSize3
                            ),
                            date: isCounterDateChanged(
                              data.port3StartDate,
                              data.port3EndDate,
                              data.counterStartDate3,
                              data.counterEndDate3,
                              data.addAld3,
                              data.counterAddAld3
                            ),
                          },
                        }
                      )}
                    </td>
                    <td
                      className={`fs-12 py-2 pe-2 align-top ${alphaDyedChanged || alphaChanged ? 'fw-semibold' : ''}`}
                      style={{
                        color:
                          alphaDyedChanged || alphaChanged
                            ? COUNTER_ACCENT
                            : undefined,
                        minWidth: 0,
                      }}
                    >
                      {usesAlphaField
                        ? formatAlphaPrice(counterAlpha, alphaSuffix)
                        : formatAlphaPrice(counterAlphaDyed, alphaSuffix)}
                    </td>
                    {usesDyedUndyed && (
                      <td
                        className={`fs-12 py-2 pe-2 align-top ${alphaUndyedChanged ? 'fw-semibold' : ''}`}
                        style={{
                          color: alphaUndyedChanged
                            ? COUNTER_ACCENT
                            : undefined,
                          minWidth: 0,
                        }}
                      >
                        {formatAlphaPrice(counterAlphaUndyed, alphaSuffix)}
                      </td>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  if (!items.length) {
    return (
      <CardBody className='p-3'>
        <div className='text-center py-4 text-muted fs-13'>
          No delivery detail available.
        </div>
      </CardBody>
    );
  }

  return (
    <CardBody className='p-3'>
      <div className='table-responsive table-card border rounded-3'>
        <table
          className='table table-hover table-centered align-middle mb-0'
          style={{ minWidth: usesDyedUndyed ? '1320px' : '1170px' }}
        >
          <thead className='table-light'>
            <tr>
              <th className={headerCellClass} style={{ width: '44px' }} />
              <th className={headerCellClass} style={{ minWidth: '110px' }}>
                Delivery Basis
              </th>
              <th className={headerCellClass} style={{ minWidth: '130px' }}>
                Incoterm - Route
              </th>
              <th className={headerCellClass} style={{ minWidth: '140px' }}>
                Loading Port
              </th>
              <th className={headerCellClass} style={{ minWidth: '180px' }}>
                Port 1
              </th>
              <th className={headerCellClass} style={{ minWidth: '180px' }}>
                Port 2
              </th>
              <th className={headerCellClass} style={{ minWidth: '120px' }}>
                Port 3
              </th>
              <th
                className={`${headerCellClass} text-center`}
                style={{ minWidth: '100px' }}
              >
                Is Capable?
              </th>
              <th className={headerCellClass} style={{ minWidth: '150px' }}>
                {usesDyedUndyed ? 'Alpha Dyed' : alphaColumnLabel}
              </th>
              {usesDyedUndyed && (
                <th className={headerCellClass} style={{ minWidth: '150px' }}>
                  Alpha Undyed
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((data, index) => {
              const rowKey = getRowKey(data, index);
              const isCapable = resolveRowCapable(data, usesAlphaField);
              const isExpanded = !!expandedRows[rowKey];
              const groupHasCounter = hasMeaningfulCounter(
                data,
                usesDyedUndyed
              );
              const groupCountered =
                !!counterByIncotermGroup[getIncotermGroupKey(data, index)];

              return (
                <React.Fragment key={rowKey}>
                  <tr
                    className={
                      isExpanded && groupHasCounter
                        ? 'border-bottom-1'
                        : undefined
                    }
                    style={
                      isExpanded && groupHasCounter
                        ? { borderLeft: `3px solid ${COUNTER_ACCENT}` }
                        : undefined
                    }
                  >
                    <td className={cellClass}>
                      {groupHasCounter ? (
                        <button
                          type='button'
                          className='btn btn-link btn-sm p-0 text-muted'
                          onClick={() => toggleRowExpand(rowKey)}
                        >
                          <i
                            className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line fs-16`}
                          />
                        </button>
                      ) : null}
                    </td>
                    <td className={`${cellClass} fw-medium fs-13`}>
                      {data.deliveryBasis || `D${index + 1}`}
                    </td>
                    <td className={`${cellClass} fs-13`}>
                      {formatIncotermRoute(data.incoterm, data.route)}
                    </td>
                    <td className={`${cellClass} fs-13 fw-medium`}>
                      {resolveLoadingPortLabel(data)}
                    </td>
                    <td className={cellClass}>
                      {renderPortCell(
                        data.port1 || data.loadingPort,
                        data.parcelSize,
                        {
                          portLabel: 'Location',
                          startDate: data.port1StartDate,
                          endDate: data.port1EndDate,
                          fallbackDate: data.addAld,
                          locationNames: data.fobLocationNames,
                          route: data.route,
                        }
                      )}
                    </td>
                    <td className={cellClass}>
                      {renderPortCell(data.port2, data.parcelSize2, {
                        startDate: data.port2StartDate,
                        endDate: data.port2EndDate,
                        fallbackDate: data.addAld2,
                      })}
                    </td>
                    <td className={cellClass}>
                      {renderPortCell(data.port3, data.parcelSize3, {
                        startDate: data.port3StartDate,
                        endDate: data.port3EndDate,
                        fallbackDate: data.addAld3,
                      })}
                    </td>
                    <td className={`${cellClass} text-center`}>
                      {isCapable ? (
                        <Badge
                          color='primary'
                          className='px-2 py-1 fs-12 fw-medium rounded-pill'
                        >
                          Compliant
                        </Badge>
                      ) : groupCountered ? (
                        <span
                          className='badge rounded-pill px-2 py-1 fs-12 fw-medium'
                          style={{
                            backgroundColor: COUNTER_ACCENT,
                            color: '#fff',
                          }}
                        >
                          Countered
                        </span>
                      ) : (
                        <Badge
                          color='secondary'
                          className='px-2 py-1 fs-12 fw-medium rounded-pill'
                        >
                          Not Offered
                        </Badge>
                      )}
                    </td>
                    <td className={`${cellClass} fs-13`}>
                      {usesAlphaField
                        ? formatAlphaPrice(data.alpha, alphaSuffix)
                        : formatAlphaPrice(
                            data.dyed ?? data.unitBbl,
                            alphaSuffix
                          )}
                    </td>
                    {usesDyedUndyed && (
                      <td className={`${cellClass} fs-13`}>
                        {formatAlphaPrice(data.undyed ?? data.cbm, alphaSuffix)}
                      </td>
                    )}
                  </tr>
                  {isExpanded && groupHasCounter && renderCounterOfferRow(data)}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </CardBody>
  );
};

export default DeliveryDetailTableNonLpg;
